import { useState, useEffect, useCallback, Fragment } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search,
  TrendingUp,
  TriangleAlert,
  ChevronDown,
  ChevronRight,
  Gift,
  Loader2,
  Check,
  Wallet,
  Sparkles,
  ChevronsUpDown,
  Trash2,
  Plus,
  Save,
  FileDown,
  FolderOpen,
  Shuffle,
} from "lucide-react";

const SKU_TYPES = [
  { id: "ALL", label: "ALL", color: "bg-slate-100 text-slate-800 border-slate-300" },
  { id: "TIN", label: "TIN", color: "bg-blue-100 text-blue-800 border-blue-300" },
  { id: "BUCKET", label: "BUCKET", color: "bg-green-100 text-green-800 border-green-300" },
  { id: "TIN_REJ", label: "TIN REJ", color: "bg-red-100 text-red-800 border-red-300" },
  { id: "VACUUM_10", label: "VACUUM 10", color: "bg-purple-100 text-purple-800 border-purple-300" },
  { id: "VACUUM_20", label: "VACUUM 20", color: "bg-orange-100 text-orange-800 border-orange-300" },
];

const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
};

const skuBadge = (sku) => {
  const s = SKU_TYPES.find((x) => x.id === sku);
  return (
    <Badge variant="outline" className={`text-[10px] font-semibold ${s ? s.color : ""}`}>
      {(sku || "").replace("_", " ")}
    </Badge>
  );
};

const monthLabel = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(m) - 1] || m} ${y}`;
};

function MonthlyHistory({ history }) {
  const [openMonths, setOpenMonths] = useState({});
  const groups = {};
  (history || []).forEach((h) => {
    const ym = (h.date || "").slice(0, 7);
    (groups[ym] = groups[ym] || []).push(h);
  });
  const months = Object.keys(groups).sort().reverse();
  return (
    <div className="space-y-1.5" data-testid="monthly-history">
      {months.map((ym) => {
        const rows = groups[ym];
        const qtySum = rows.reduce((s, h) => s + (h.qty || 0), 0);
        const valSum = rows.reduce((s, h) => s + (h.qty || 0) * (h.rate || 0), 0);
        const avg = qtySum ? Math.round(valSum / qtySum) : 0;
        const isOpen = openMonths[ym] ?? (ym === months[0]);
        return (
          <div key={ym} className="border rounded-md overflow-hidden bg-white">
            <button
              onClick={() => setOpenMonths((o) => ({ ...o, [ym]: !isOpen }))}
              className="w-full flex items-center justify-between px-3 py-1.5 bg-muted/60 hover:bg-muted"
              data-testid={`month-header-${ym}`}
            >
              <span className="flex items-center gap-1.5 text-xs font-semibold">
                {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                {monthLabel(ym)}
                <span className="text-[10px] font-normal text-muted-foreground">({rows.length} dispatches · {qtySum} qty)</span>
              </span>
              <span className="text-xs font-bold text-emerald-700">Avg {inr(avg)}</span>
            </button>
            {isOpen && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs h-7">Date</TableHead>
                    <TableHead className="text-xs h-7">Dispatch #</TableHead>
                    <TableHead className="text-xs h-7">Customer</TableHead>
                    <TableHead className="text-xs h-7 text-right">Qty</TableHead>
                    <TableHead className="text-xs h-7 text-right">Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((h, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs py-1.5">{fmtDate(h.date)}</TableCell>
                      <TableCell className="text-xs py-1.5 font-mono">{h.dispatch_number || "—"}</TableCell>
                      <TableCell className="text-xs py-1.5">{h.customer || "—"}</TableCell>
                      <TableCell className="text-xs py-1.5 text-right tabular-nums">{h.qty}</TableCell>
                      <TableCell className="text-xs py-1.5 text-right tabular-nums font-medium">{inr(h.rate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Strategy() {
  const [activeTab, setActiveTab] = useState("rate-history");
  const [rateData, setRateData] = useState([]);
  const [belowCost, setBelowCost] = useState([]);
  const [costSettings, setCostSettings] = useState({ TIN: "", BUCKET: "", TIN_REJ: "" });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [skuFilter, setSkuFilter] = useState("ALL");
  const [expanded, setExpanded] = useState(null);
  const [savingCost, setSavingCost] = useState(false);
  const [belowSku, setBelowSku] = useState("ALL");
  const [sellPerKg, setSellPerKg] = useState("");
  const [savingSell, setSavingSell] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rh, bc, cs, sell] = await Promise.all([
        axios.get(`${API}/strategy/rate-history`),
        axios.get(`${API}/strategy/below-cost`),
        axios.get(`${API}/strategy/cost-settings`),
        axios.get(`${API}/strategy/sell-settings`),
      ]);
      setRateData(rh.data.grades || []);
      setBelowCost(bc.data.grades || []);
      const cpk = cs.data.cost_per_kg || {};
      setCostSettings({
        TIN: cpk.TIN || "",
        BUCKET: cpk.BUCKET || "",
        TIN_REJ: cpk.TIN_REJ || "",
      });
      setSellPerKg(sell.data.sell_per_kg || "");
    } catch (e) {
      toast.error("Failed to load strategy data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveSellSetting = async () => {
    setSavingSell(true);
    try {
      await axios.put(`${API}/strategy/sell-settings`, { sell_per_kg: parseFloat(sellPerKg) || 0 });
      toast.success("Sell avg/kg saved — used as combo target");
    } catch (e) {
      toast.error("Failed to save sell avg/kg");
    } finally {
      setSavingSell(false);
    }
  };

  const saveCostSettings = async () => {
    setSavingCost(true);
    try {
      await axios.put(`${API}/strategy/cost-settings`, {
        cost_per_kg: {
          TIN: parseFloat(costSettings.TIN) || 0,
          BUCKET: parseFloat(costSettings.BUCKET) || 0,
          TIN_REJ: parseFloat(costSettings.TIN_REJ) || 0,
        },
      });
      toast.success("Average cost prices saved");
      const bc = await axios.get(`${API}/strategy/below-cost`);
      setBelowCost(bc.data.grades || []);
    } catch (e) {
      toast.error("Failed to save cost prices");
    } finally {
      setSavingCost(false);
    }
  };

  const keyOf = (r) => `${r.grade}|${r.sku}`;
  const filteredRates = rateData.filter((r) => {
    const matchSku = skuFilter === "ALL" || r.sku === skuFilter;
    const matchSearch = !search || r.grade.toLowerCase().includes(search.toLowerCase());
    return matchSku && matchSearch;
  });

  return (
    <div className="p-6" data-testid="strategy-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>
            Strategy
          </h1>
          <p className="text-sm text-muted-foreground">
            Dispatch rate history, cost pricing & margin analysis
          </p>
        </div>

        <div className="flex items-center gap-2">
        {/* Sell Avg / Kg (combo target) */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="sell-settings-trigger">
              <TrendingUp className="w-4 h-4" />
              Sell Avg / Kg
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64" align="end">
            <div className="space-y-2">
              <div className="text-sm font-semibold">Sell Average Price (per Kg)</div>
              <p className="text-xs text-muted-foreground">Used as the default target ASP for combo offers.</p>
              <Input type="number" value={sellPerKg}
                onChange={(e) => setSellPerKg(e.target.value)}
                placeholder="₹ / kg" className="h-8 tabular-nums"
                data-testid="sell-input" />
              <Button size="sm" className="w-full" onClick={saveSellSetting} disabled={savingSell} data-testid="sell-settings-save">
                {savingSell ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Average Cost / Kg settings */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="cost-settings-trigger">
              <Wallet className="w-4 h-4" />
              Avg Cost / Kg
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72" align="end">
            <div className="space-y-3">
              <div className="text-sm font-semibold">Average Cost Price (per Kg)</div>
              <p className="text-xs text-muted-foreground">
                Single cost/kg applied to all grades of each category.
              </p>
              {["TIN", "BUCKET", "TIN_REJ"].map((c) => (
                <div key={c} className="flex items-center gap-2">
                  <Label className="text-xs w-20">{c.replace("_", " ")}</Label>
                  <Input
                    type="number"
                    value={costSettings[c]}
                    onChange={(e) => setCostSettings((s) => ({ ...s, [c]: e.target.value }))}
                    placeholder="₹ / kg"
                    className="h-8 tabular-nums"
                    data-testid={`cost-input-${c}`}
                  />
                </div>
              ))}
              <Button
                size="sm"
                className="w-full"
                onClick={saveCostSettings}
                disabled={savingCost}
                data-testid="cost-settings-save"
              >
                {savingCost ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 bg-muted rounded-lg p-0.5 w-fit" data-testid="strategy-tabs">
        <Button variant={activeTab === "rate-history" ? "default" : "ghost"} size="sm"
          className="h-8 px-4 text-xs gap-1.5" onClick={() => setActiveTab("rate-history")} data-testid="tab-rate-history">
          <TrendingUp className="w-3.5 h-3.5" /> Rate History
        </Button>
        <Button variant={activeTab === "below-cost" ? "default" : "ghost"} size="sm"
          className="h-8 px-4 text-xs gap-1.5" onClick={() => setActiveTab("below-cost")} data-testid="tab-below-cost">
          <TriangleAlert className="w-3.5 h-3.5" /> Below Cost
          {belowCost.length > 0 && (
            <Badge variant="secondary" className="ml-1 bg-red-100 text-red-700 text-[10px]">{belowCost.length}</Badge>
          )}
        </Button>
        <Button variant={activeTab === "combo" ? "default" : "ghost"} size="sm"
          className="h-8 px-4 text-xs gap-1.5" onClick={() => setActiveTab("combo")} data-testid="tab-combo-offers">
          <Gift className="w-3.5 h-3.5" /> Combo Offers
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
        </div>
      ) : (
        <>
          {/* RATE HISTORY TAB */}
          {activeTab === "rate-history" && (
            <div className="w-full xl:max-w-[52%]">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search grade..." value={search} onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-9" data-testid="rate-history-search" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {SKU_TYPES.map((sku) => (
                    <Button key={sku.id} variant={skuFilter === sku.id ? "default" : "outline"} size="sm"
                      className={`h-8 text-xs ${skuFilter === sku.id ? "" : sku.color}`}
                      onClick={() => setSkuFilter(sku.id)} data-testid={`sku-filter-${sku.id}`}>
                      {sku.label}
                    </Button>
                  ))}
                </div>
              </div>

              <Card className="border-border/60" data-testid="rate-history-card">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-8"></TableHead>
                        <TableHead className="font-bold">Grade</TableHead>
                        <TableHead className="font-bold">SKU</TableHead>
                        <TableHead className="font-bold text-right">Dispatched Qty</TableHead>
                        <TableHead className="font-bold text-right">Latest Rate</TableHead>
                        <TableHead className="font-bold text-right">Avg Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRates.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                            No dispatch rate history found.
                          </TableCell>
                        </TableRow>
                      )}
                      {filteredRates.map((r) => {
                        const k = keyOf(r);
                        const isOpen = expanded === k;
                        return (
                          <Fragment key={k}>
                            <TableRow data-testid={`rate-row-${r.grade}-${r.sku}`}>
                              <TableCell className="py-2">
                                <button onClick={() => setExpanded(isOpen ? null : k)}
                                  className="text-muted-foreground hover:text-foreground"
                                  data-testid={`expand-${r.grade}-${r.sku}`}>
                                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                </button>
                              </TableCell>
                              <TableCell className="font-semibold py-2">{r.grade}</TableCell>
                              <TableCell className="py-2">{skuBadge(r.sku)}</TableCell>
                              <TableCell className="text-right py-2 tabular-nums">
                                {Math.round(r.dispatched_qty).toLocaleString("en-IN")}
                              </TableCell>
                              <TableCell className="text-right py-2 tabular-nums font-medium">
                                {inr(r.latest_rate)}
                                <div className="text-[10px] text-muted-foreground">{fmtDate(r.latest_date)}</div>
                              </TableCell>
                              <TableCell className="text-right py-2 tabular-nums text-muted-foreground">
                                {inr(r.avg_rate)}
                              </TableCell>
                            </TableRow>
                            {isOpen && (
                              <TableRow key={`${k}-detail`} className="bg-muted/30">
                                <TableCell colSpan={6} className="p-0">
                                  <div className="p-4">
                                    <div className="text-xs font-semibold mb-2 text-muted-foreground">
                                      Dispatch Rate History — {r.grade} {r.sku} ({r.trade_count} dispatches)
                                    </div>
                                    <MonthlyHistory history={r.history} />
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* BELOW COST TAB */}
          {activeTab === "below-cost" && (
            <div>
              <div className="flex flex-wrap gap-2 mb-4">
                {["ALL", "TIN", "BUCKET", "TIN_REJ"].map((s) => {
                  const meta = SKU_TYPES.find((x) => x.id === s);
                  const count = s === "ALL" ? belowCost.length : belowCost.filter((r) => r.sku === s).length;
                  return (
                    <Button key={s} variant={belowSku === s ? "default" : "outline"} size="sm"
                      className={`h-8 text-xs ${belowSku === s ? "" : meta ? meta.color : ""}`}
                      onClick={() => setBelowSku(s)} data-testid={`below-sku-${s}`}>
                      {s.replace("_", " ")}
                      <Badge variant="secondary" className="ml-1.5 bg-white/60 text-[10px]">{count}</Badge>
                    </Button>
                  );
                })}
              </div>
              <Card className="border-border/60" data-testid="below-cost-card">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-bold">Grade</TableHead>
                      <TableHead className="font-bold">SKU</TableHead>
                      <TableHead className="font-bold text-right">Pack (kg)</TableHead>
                      <TableHead className="font-bold text-right">Selling Price</TableHead>
                      <TableHead className="font-bold text-right">Cost Price</TableHead>
                      <TableHead className="font-bold text-right">Loss / Pack</TableHead>
                      <TableHead className="font-bold text-right">Dispatched Qty</TableHead>
                      <TableHead className="font-bold">Last Sold</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {belowCost.filter((r) => belowSku === "ALL" || r.sku === belowSku).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-14 text-muted-foreground">
                          <Check className="w-8 h-8 mx-auto mb-2 text-green-500" />
                          No grades are selling below cost. Set the Avg Cost / Kg (top-right) to track margins.
                        </TableCell>
                      </TableRow>
                    )}
                    {belowCost.filter((r) => belowSku === "ALL" || r.sku === belowSku).map((r) => (
                      <TableRow key={`${r.grade}-${r.sku}`} className="bg-red-50/40" data-testid={`below-cost-row-${r.grade}-${r.sku}`}>
                        <TableCell className="font-semibold">{r.grade}</TableCell>
                        <TableCell>{skuBadge(r.sku)}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{r.pack_kg}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-red-600">
                          {inr(r.selling_price)}
                          <div className="text-[10px] text-muted-foreground font-normal">{inr(r.selling_per_kg)}/kg</div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {inr(r.cost_price)}
                          <div className="text-[10px] text-muted-foreground">{inr(r.cost_per_kg)}/kg</div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-bold text-red-600">
                          -{inr(r.loss_per_pack)}
                          <div className="text-[10px] text-red-400 font-normal">-{inr(r.loss_per_kg)}/kg</div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{Math.round(r.dispatched_qty).toLocaleString("en-IN")}</TableCell>
                        <TableCell className="text-xs">
                          {fmtDate(r.latest_date)}
                          <div className="text-[10px] text-muted-foreground">{r.latest_customer}</div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            </div>
          )}

          {/* COMBO OFFERS TAB */}
          {activeTab === "combo" && <ComboOffers defaultTarget={sellPerKg} />}
        </>
      )}
    </div>
  );
}

// ------------------ Combo Offers ------------------
function ComboOffers({ defaultTarget }) {
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [totalQty, setTotalQty] = useState("");
  const [targetAvg, setTargetAvg] = useState("");
  const [generating, setGenerating] = useState(false);
  const [lines, setLines] = useState(null);
  const [available, setAvailable] = useState([]);
  const [message, setMessage] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [currentTplId, setCurrentTplId] = useState(null);
  const [savingTpl, setSavingTpl] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [costMap, setCostMap] = useState({});

  const loadTemplateList = async (name) => {
    try {
      const res = await axios.get(`${API}/strategy/combo-templates`, { params: { customer: name } });
      setTemplates(res.data.templates || []);
    } catch (e) {
      setTemplates([]);
    }
  };

  const selectCustomer = (name) => {
    setCustomer(name);
    setCustOpen(false);
    setCurrentTplId(null);
    loadTemplateList(name);
  };

  const loadTemplate = (tpl) => {
    if (tpl.total_qty) setTotalQty(String(tpl.total_qty));
    if (tpl.target_avg) setTargetAvg(String(tpl.target_avg));
    setLines((tpl.lines || []).map((l) => ({
      uid: `${l.grade}|${l.sku}`,
      grade: l.grade, sku: l.sku, source: l.source,
      customerRate: l.customerRate, offeredRate: l.offeredRate,
      qty: l.qty, stock: l.stock, pack_kg: l.pack_kg,
    })));
    setCurrentTplId(tpl.id);
    setTplName(tpl.name);
    setMessage("");
    toast.success(`Loaded "${tpl.name}"`);
  };

  const openSaveDialog = (asNew) => {
    if (asNew) { setCurrentTplId(null); setTplName(""); }
    else setTplName(tplName || "");
    setTplDialogOpen(true);
  };

  const confirmSaveTemplate = async () => {
    if (!tplName.trim()) return toast.error("Enter a template name");
    setSavingTpl(true);
    try {
      const res = await axios.post(`${API}/strategy/combo-template`, {
        id: currentTplId || undefined,
        customer,
        name: tplName.trim(),
        total_qty: parseFloat(totalQty) || 0,
        target_avg: parseFloat(targetAvg) || 0,
        lines: lines.map((l) => ({
          grade: l.grade, sku: l.sku, source: l.source,
          customerRate: safe(l.customerRate), offeredRate: safe(l.offeredRate),
          qty: safe(l.qty), stock: l.stock, pack_kg: l.pack_kg,
        })),
      });
      setCurrentTplId(res.data.id);
      toast.success(`Combo "${tplName.trim()}" saved`);
      setTplDialogOpen(false);
      loadTemplateList(customer);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save combo");
    } finally {
      setSavingTpl(false);
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await axios.delete(`${API}/strategy/combo-template/${id}`);
      if (currentTplId === id) setCurrentTplId(null);
      loadTemplateList(customer);
      toast.success("Template deleted");
    } catch (e) {
      toast.error("Failed to delete template");
    }
  };

  const shareQuote = async () => {
    if (!lines || lines.length === 0) return toast.error("Nothing to quote");
    setSharing(true);
    try {
      const res = await axios.post(`${API}/strategy/combo-quote`, {
        customer,
        target_avg: parseFloat(targetAvg) || 0,
        lines: lines.map((l) => ({
          grade: l.grade, sku: l.sku, qty: safe(l.qty), offeredRate: safe(l.offeredRate),
        })),
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Combo_Quote_${customer.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Quote PDF downloaded");
    } catch (e) {
      toast.error("Failed to generate quote");
    } finally {
      setSharing(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [cRes, sRes, costRes] = await Promise.all([
          axios.get(`${API}/customers`),
          axios.get(`${API}/strategy/combo-settings`),
          axios.get(`${API}/strategy/cost-settings`),
        ]);
        setCustomers(cRes.data || []);
        if (sRes.data.total_qty) setTotalQty(String(sRes.data.total_qty));
        if (sRes.data.target_avg) setTargetAvg(String(sRes.data.target_avg));
        else if (defaultTarget) setTargetAvg(String(defaultTarget));
        setCostMap(costRes.data.cost_per_kg || {});
      } catch (e) {
        // silent
      }
    })();
  }, []);

  const shuffle = () => {
    if (!lines || lines.length === 0) return;
    const rnd = (a, b) => a + Math.random() * (b - a);
    let ls = lines.map((l) => {
      // qty jitter ±20-30%
      let q = Math.round(safe(l.qty) * rnd(0.7, 1.3));
      if (l.source === "high-stock") q = Math.max(2, q - (q % 2));
      else q = Math.max(1, q);
      if (l.stock) q = Math.min(q, Math.floor(l.stock));
      // rate jitter (mostly lower by 1-2%)
      let r = Math.round(safe(l.offeredRate) * rnd(0.975, 1.01));
      return { ...l, qty: q, offeredRate: r };
    });
    // nudge rates so the bundle ASP lands on target (keeps average maintained)
    const T = parseFloat(targetAvg);
    if (T > 0) {
      const kg = ls.reduce((s, l) => s + l.qty * (l.pack_kg || 0), 0);
      const val = ls.reduce((s, l) => s + l.qty * l.offeredRate, 0);
      const asp = kg ? val / kg : 0;
      if (asp > 0) {
        const scale = T / asp;
        ls = ls.map((l) => ({ ...l, offeredRate: Math.round(l.offeredRate * scale) }));
      }
    }
    setLines(ls);
    toast.success("Shuffled — fresh rates & quantities");
  };

  const generate = async () => {
    if (!customer) return toast.error("Select a customer");
    if (!totalQty || parseFloat(totalQty) <= 0) return toast.error("Enter total qty (units)");
    if (!targetAvg || parseFloat(targetAvg) <= 0) return toast.error("Enter target avg price/kg");
    setGenerating(true);
    try {
      await axios.put(`${API}/strategy/combo-settings`, {
        total_qty: parseFloat(totalQty),
        target_avg: parseFloat(targetAvg),
      });
      const res = await axios.post(`${API}/strategy/combo-suggest`, {
        customer,
        total_qty: parseFloat(totalQty),
        target_avg: parseFloat(targetAvg),
      });
      setLines((res.data.items || []).map((it) => ({
        uid: `${it.grade}|${it.sku}`,
        grade: it.grade, sku: it.sku, source: it.source,
        customerRate: it.customer_rate ?? it.rate,
        offeredRate: it.offered_rate ?? it.rate,
        qty: it.qty, stock: it.stock, pack_kg: it.pack_kg,
      })));
      setAvailable(res.data.available_grades || []);
      setMessage(res.data.message || "");
      if (res.data.message) toast.warning(res.data.message);
      else toast.success("Smart Combo generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate combo");
    } finally {
      setGenerating(false);
    }
  };

  const updateLine = (uid, field, raw) => {
    setLines((ls) => ls.map((l) => {
      if (l.uid !== uid) return l;
      let v = raw === "" ? "" : parseFloat(raw);
      if (field === "qty" && v !== "" && l.stock && v > l.stock) v = l.stock; // fit-to-stock
      if (field === "qty" && v !== "" && v < 0) v = 0;
      return { ...l, [field]: v };
    }));
  };

  const removeLine = (uid) => setLines((ls) => ls.filter((l) => l.uid !== uid));

  const addGrade = (g) => {
    const uid = `${g.grade}|${g.sku}`;
    setLines((ls) => {
      if (ls.some((l) => l.uid === uid)) return ls;
      const defQty = g.source === "high-stock" ? Math.min(2, g.stock || 2) : 1;
      return [...ls, {
        uid, grade: g.grade, sku: g.sku, source: g.source,
        customerRate: g.rate, offeredRate: g.rate,
        qty: Math.min(defQty, g.stock || defQty) || defQty,
        stock: g.stock, pack_kg: g.pack_kg,
      }];
    });
    setAddOpen(false);
  };

  // Live summary
  const safe = (n) => (n === "" || isNaN(n) ? 0 : n);
  const totalUnits = lines ? lines.reduce((s, l) => s + safe(l.qty), 0) : 0;
  const totalKg = lines ? lines.reduce((s, l) => s + safe(l.qty) * (l.pack_kg || 0), 0) : 0;
  const totalValue = lines ? lines.reduce((s, l) => s + safe(l.qty) * safe(l.offeredRate), 0) : 0;
  const avgPpk = totalKg ? totalValue / totalKg : 0;
  const meets = targetAvg ? avgPpk >= parseFloat(targetAvg) - 0.01 : false;
  const addable = available.filter((g) => !lines?.some((l) => l.uid === `${g.grade}|${g.sku}`));

  // Profit per line = qty * (offered rate - cost/kg * pack). Null if cost/kg not set for that SKU.
  const lineProfit = (l) => {
    const cpk = costMap[l.sku];
    if (!cpk || cpk <= 0 || !l.pack_kg) return null;
    return safe(l.qty) * (safe(l.offeredRate) - cpk * l.pack_kg);
  };
  const hasCost = lines ? lines.some((l) => lineProfit(l) !== null) : false;
  const totalProfit = lines ? lines.reduce((s, l) => s + (lineProfit(l) || 0), 0) : 0;

  // Dynamic single-line advice to reach the target ASP (updates on every edit)
  let advice = "";
  if (lines && lines.length && totalKg > 0 && targetAvg) {
    const T = parseFloat(targetAvg);
    const V = totalValue, K = totalKg;
    const gap = T - avgPpk;
    if (gap > 0.5) {
      const needed = T * K - V; // value shortfall
      const enrich = (l) => ({
        grade: l.grade, sku: l.sku, qty: safe(l.qty), rate: safe(l.offeredRate),
        pack: l.pack_kg, stock: l.stock || 0,
        ppk: safe(l.offeredRate) / l.pack_kg, margin: safe(l.offeredRate) - T * l.pack_kg,
      });
      const cur = lines.filter((l) => safe(l.qty) > 0 && l.pack_kg > 0).map(enrich);

      // Option 1: cut cheapest (negative-margin) units until target met
      let remCut = needed, cutN = 0, cutGrade = "";
      for (const c of [...cur].sort((a, b) => a.ppk - b.ppk)) {
        if (c.margin >= 0) break;
        let u = c.qty;
        if (!cutGrade) cutGrade = `${c.grade} ${c.sku.replace("_", " ")}`;
        while (u > 0 && remCut > 0) { remCut += c.margin; u--; cutN++; }
        if (remCut <= 0) break;
      }

      // Option 2: add units of the highest-margin grade (existing line room or available stock)
      const premPool = [
        ...cur.filter((c) => c.margin > 0).map((c) => ({ ...c, room: Math.max(0, c.stock - c.qty) })),
        ...available
          .filter((g) => g.pack_kg > 0 && !lines.some((l) => l.uid === `${g.grade}|${g.sku}`))
          .map((g) => ({ grade: g.grade, sku: g.sku, rate: g.rate, pack: g.pack_kg, margin: g.rate - T * g.pack_kg, room: g.stock || 0 })),
      ].filter((p) => p.margin > 0 && p.room > 0).sort((a, b) => b.margin - a.margin);
      let addTxt = "";
      if (premPool.length) {
        const p = premPool[0];
        const yAdd = Math.min(Math.ceil(needed / p.margin), p.room);
        if (yAdd >= 1) addTxt = `add ~${yAdd} units of ${p.grade} ${p.sku.replace("_", " ")}`;
      }
      const cutTxt = (cutN >= 1 && cutGrade) ? `cut ~${cutN} units of ${cutGrade}` : "";
      const opts = [addTxt, cutTxt, `raise offered rates ~₹${Math.round(gap)}/kg overall`].filter(Boolean);
      advice = `₹${Math.round(gap)}/kg below target — ${opts.join(", or ")}, to reach ₹${Math.round(T)}/kg.`;
    } else {
      const surplus = avgPpk - T;
      const surplusValue = V - T * K;
      const pick = available
        .filter((g) => !lines.some((l) => l.uid === `${g.grade}|${g.sku}`) && g.pack_kg > 0 && (g.stock || 0) > 0)
        .map((g) => ({ g, ppk: g.rate / g.pack_kg }))
        .sort((a, b) => a.ppk - b.ppk)[0];
      if (pick && pick.g.rate - T * pick.g.pack_kg < 0) {
        const yMax = Math.floor(surplusValue / (T * pick.g.pack_kg - pick.g.rate));
        const y = Math.max(0, Math.min(yMax, Math.floor(pick.g.stock || 0)));
        advice = y >= 1
          ? `✓ ₹${Math.round(surplus)}/kg above target — you can add ~${y} units of ${pick.g.grade} ${pick.g.sku.replace("_", " ")} (high stock) and stay on target.`
          : `✓ On target — ₹${Math.round(surplus)}/kg headroom to add more stock.`;
      } else {
        advice = `✓ On target — ₹${Math.round(surplus)}/kg headroom.`;
      }
    }
  }

  return (
    <div className="space-y-3 w-full xl:max-w-[54%] lg:max-w-[72%]" data-testid="combo-offers-card">
      {/* Builder form */}
      <Card className="border-border/60">
        <CardContent className="p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold">Smart Combo Builder</span>
          </div>
          <div>
            <Label className="text-xs">Customer</Label>
            <Popover open={custOpen} onOpenChange={setCustOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between h-9 mt-1 font-normal"
                  data-testid="combo-customer-select">
                  <span className="truncate">{customer || "Select customer..."}</span>
                  <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search customer..." />
                  <CommandList>
                    <CommandEmpty>No customer found.</CommandEmpty>
                    <CommandGroup>
                      {customers.map((c) => (
                        <CommandItem key={c.id} value={c.name}
                          onSelect={() => selectCustomer(c.name)}
                          data-testid={`combo-cust-${c.id}`}>
                          <Check className={`mr-2 h-4 w-4 ${customer === c.name ? "opacity-100" : "opacity-0"}`} />
                          {c.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Total Qty (approx.)</Label>
              <Input type="number" value={totalQty} onChange={(e) => setTotalQty(e.target.value)}
                placeholder="e.g. 50" className="h-9 mt-1 tabular-nums" data-testid="combo-total-qty" />
            </div>
            <div>
              <Label className="text-xs">Target Avg / Kg</Label>
              <Input type="number" value={targetAvg} onChange={(e) => setTargetAvg(e.target.value)}
                placeholder="e.g. 750" className="h-9 mt-1 tabular-nums" data-testid="combo-target-avg" />
            </div>
          </div>
          <Button onClick={generate} disabled={generating} className="h-9 w-full gap-1.5" data-testid="combo-generate">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Generate Combo
          </Button>
          {customer && templates.length > 0 && (
            <div className="pt-1" data-testid="combo-templates-list">
              <div className="text-[11px] font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                <FolderOpen className="w-3.5 h-3.5" /> Saved combos for {customer}
              </div>
              <div className="space-y-1">
                {templates.map((t) => (
                  <div key={t.id} className={`flex items-center gap-2 rounded-md border px-2 py-1 ${currentTplId === t.id ? "border-primary bg-primary/5" : "border-border/60"}`}>
                    <button className="flex-1 text-left text-xs font-medium truncate hover:text-primary"
                      onClick={() => loadTemplate(t)} data-testid={`combo-tpl-load-${t.id}`}>
                      {t.name}
                      <span className="text-[10px] text-muted-foreground font-normal ml-1.5">
                        {t.total_qty} u · ₹{t.target_avg}/kg · {(t.lines || []).length} grades
                      </span>
                    </button>
                    <button className="text-muted-foreground hover:text-red-600" onClick={() => deleteTemplate(t.id)}
                      data-testid={`combo-tpl-delete-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {lines && (
        <>
          {/* Compact summary */}
          <Card className={meets ? "border-green-300 bg-green-50/40" : "border-amber-300 bg-amber-50/40"}>
            <CardContent className="p-3">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div>
                  <div className="text-[10px] text-muted-foreground">Units</div>
                  <div className="text-base font-bold" data-testid="combo-total-units">{Math.round(totalUnits)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground">Value</div>
                  <div className="text-base font-bold">{inr(totalValue)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground">Avg / Kg</div>
                  <div className={`text-base font-bold ${meets ? "text-green-700" : "text-amber-700"}`} data-testid="combo-avg-ppk">
                    {inr(avgPpk)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground">Profit</div>
                  <div className={`text-base font-bold ${totalProfit >= 0 ? "text-green-700" : "text-red-600"}`} data-testid="combo-total-profit">
                    {hasCost ? inr(totalProfit) : "—"}
                  </div>
                </div>
              </div>
              <div className="text-center text-[11px] mt-1">
                {meets
                  ? <span className="text-green-700 font-medium">✓ Meets target ₹{targetAvg}/kg · {Math.round(totalKg)} kg · {lines.length} grades</span>
                  : <span className="text-amber-700 font-medium">Below target ₹{targetAvg}/kg · {Math.round(totalKg)} kg · {lines.length} grades</span>}
              </div>
              {advice && (
                <div className={`mt-2 text-[11px] rounded-md px-2 py-1.5 flex items-start gap-1.5 ${meets ? "bg-green-100/70 text-green-800" : "bg-amber-100/70 text-amber-900"}`} data-testid="combo-advice">
                  <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{advice}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {message && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">{message}</div>
          )}

          {/* Editable bundle */}
          <Card className="border-border/60">
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-3 py-2 border-b">
                <span className="text-xs font-semibold text-muted-foreground">
                  Bundle · {lines.length} grades{currentTplId ? ` · ${tplName}` : ""}
                </span>
                <div className="flex items-center gap-1.5">
                  <Button onClick={shuffle} variant="outline" size="sm" className="h-7 gap-1.5 text-xs" data-testid="combo-shuffle">
                    <Shuffle className="w-3.5 h-3.5" /> Shuffle
                  </Button>
                  <Button onClick={shareQuote} disabled={sharing} variant="outline" size="sm" className="h-7 gap-1.5 text-xs" data-testid="combo-share-pdf">
                    {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                    Share PDF
                  </Button>
                  {currentTplId && (
                    <Button onClick={() => openSaveDialog(false)} variant="outline" size="sm" className="h-7 gap-1.5 text-xs" data-testid="combo-update-template">
                      <Save className="w-3.5 h-3.5" /> Update
                    </Button>
                  )}
                  <Button onClick={() => openSaveDialog(true)} variant="outline" size="sm" className="h-7 gap-1.5 text-xs" data-testid="combo-save-template">
                    <Plus className="w-3.5 h-3.5" /> Save as
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-bold text-xs">Grade</TableHead>
                    <TableHead className="font-bold text-xs">SKU</TableHead>
                    <TableHead className="font-bold text-xs text-right">
                      Cust. Rate
                      <span className="block text-[9px] font-normal text-muted-foreground normal-case">market trend</span>
                    </TableHead>
                    <TableHead className="font-bold text-xs text-right">
                      Offered Rate
                      <span className="block text-[9px] font-normal text-muted-foreground normal-case">internal only</span>
                    </TableHead>
                    <TableHead className="font-bold text-xs text-right">Qty</TableHead>
                    <TableHead className="font-bold text-xs text-right">Value</TableHead>
                    <TableHead className="font-bold text-xs text-right">
                      Profit
                      <span className="block text-[9px] font-normal text-muted-foreground normal-case">vs cost/kg</span>
                    </TableHead>
                    <TableHead className="w-8"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground text-xs">
                        No grades in this combo. Add one below.
                      </TableCell>
                    </TableRow>
                  )}
                  {lines.map((l) => (
                    <TableRow key={l.uid} data-testid={`combo-item-${l.grade}-${l.sku}`}>
                      <TableCell className="py-1.5">
                        <span className="font-semibold text-sm">{l.grade}</span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className={`text-[9px] px-1 rounded ${l.source === "frequent" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                            {l.source === "frequent" ? "Frequent" : "High stock"}
                          </span>
                          <span className="text-[9px] text-muted-foreground">stock {Math.round(l.stock || 0)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-1.5">{skuBadge(l.sku)}</TableCell>
                      <TableCell className="py-1.5 text-right tabular-nums text-xs text-muted-foreground" data-testid={`combo-custrate-${l.grade}-${l.sku}`}>
                        {inr(l.customerRate)}
                      </TableCell>
                      <TableCell className="py-1.5 text-right">
                        <Input type="number" value={l.offeredRate} onChange={(e) => updateLine(l.uid, "offeredRate", e.target.value)}
                          className="h-7 w-20 text-right tabular-nums ml-auto text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          data-testid={`combo-offered-${l.grade}-${l.sku}`} />
                      </TableCell>
                      <TableCell className="py-1.5 text-right">
                        <Input type="number" value={l.qty} onChange={(e) => updateLine(l.uid, "qty", e.target.value)}
                          max={l.stock} className="h-7 w-16 text-right tabular-nums ml-auto text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          data-testid={`combo-qty-${l.grade}-${l.sku}`} />
                      </TableCell>
                      <TableCell className="py-1.5 text-right tabular-nums text-sm font-medium">
                        {inr(safe(l.qty) * safe(l.offeredRate))}
                      </TableCell>
                      <TableCell className="py-1.5 text-right tabular-nums text-sm" data-testid={`combo-profit-${l.grade}-${l.sku}`}>
                        {lineProfit(l) === null
                          ? <span className="text-muted-foreground text-xs">—</span>
                          : <span className={lineProfit(l) >= 0 ? "text-green-700 font-medium" : "text-red-600 font-medium"}>{inr(lineProfit(l))}</span>}
                      </TableCell>
                      <TableCell className="py-1.5">
                        <button onClick={() => removeLine(l.uid)} className="text-muted-foreground hover:text-red-600"
                          data-testid={`combo-delete-${l.grade}-${l.sku}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Add grade */}
              <div className="p-2 border-t">
                <Popover open={addOpen} onOpenChange={setAddOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full h-8 gap-1.5 text-xs" data-testid="combo-add-grade-trigger">
                      <Plus className="w-3.5 h-3.5" /> Add grade
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search grade..." />
                      <CommandList>
                        <CommandEmpty>No grade found.</CommandEmpty>
                        <CommandGroup>
                          {addable.map((g) => (
                            <CommandItem key={`${g.grade}|${g.sku}`} value={`${g.grade} ${g.sku}`}
                              onSelect={() => addGrade(g)} data-testid={`combo-add-${g.grade}-${g.sku}`}>
                              <span className="font-medium mr-2">{g.grade}</span>
                              {skuBadge(g.sku)}
                              <span className="ml-auto text-[10px] text-muted-foreground">{inr(g.rate)} · stk {Math.round(g.stock || 0)}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Save template dialog */}
      <Dialog open={tplDialogOpen} onOpenChange={setTplDialogOpen}>
        <DialogContent className="sm:max-w-[400px]" data-testid="combo-save-dialog">
          <DialogHeader>
            <DialogTitle>{currentTplId ? "Update combo template" : "Save combo template"}</DialogTitle>
            <DialogDescription>Give this combo a name so you can reopen and tweak it later.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Template name</Label>
            <Input value={tplName} onChange={(e) => setTplName(e.target.value)}
              placeholder="e.g. Diwali Combo" className="h-9 mt-1"
              onKeyDown={(e) => { if (e.key === "Enter") confirmSaveTemplate(); }}
              data-testid="combo-tpl-name-input" autoFocus />
            <p className="text-[11px] text-muted-foreground mt-1">Saved under {customer}.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTplDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmSaveTemplate} disabled={savingTpl} data-testid="combo-tpl-save-confirm">
              {savingTpl ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
